'use client';

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { readThemeCookie } from '@/platform/cookies/consent';
import {
  applyTheme,
  resolveEffectiveTheme,
  THEME_DEFAULT,
} from '@/platform/theme/themeBootstrap';
import { fetchSiteAppearance } from './api';
import {
  SITE_APPEARANCE_DEFAULT,
  SITE_APPEARANCE_STORAGE_KEY,
  type SiteAppearancePublic,
} from './types';

type SiteAppearanceContextValue = {
  appearance: SiteAppearancePublic;
  loading: boolean;
  refresh: () => Promise<void>;
};

const SiteAppearanceContext = createContext<SiteAppearanceContextValue>({
  appearance: SITE_APPEARANCE_DEFAULT,
  loading: true,
  refresh: async () => {},
});

export function useSiteAppearance() {
  return useContext(SiteAppearanceContext);
}

function cacheAppearance(appearance: SiteAppearancePublic) {
  try {
    sessionStorage.setItem(SITE_APPEARANCE_STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    /* ignore quota / private mode */
  }
}

function readCachedAppearance(): SiteAppearancePublic | null {
  try {
    const raw = sessionStorage.getItem(SITE_APPEARANCE_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SiteAppearancePublic;
  } catch {
    return null;
  }
}

function applyAppearanceSideEffects(appearance: SiteAppearancePublic) {
  const root = document.documentElement;
  if (appearance.backgroundImageUrl) {
    root.style.setProperty('--site-page-backdrop', `url('${appearance.backgroundImageUrl}')`);
    if (appearance.backgroundScope === 'site') {
      root.setAttribute('data-site-backdrop', 'site');
    } else {
      root.removeAttribute('data-site-backdrop');
    }
  } else {
    root.style.removeProperty('--site-page-backdrop');
    root.removeAttribute('data-site-backdrop');
  }
}

function applyMergedTheme(appearance: SiteAppearancePublic) {
  applyTheme(resolveEffectiveTheme(readThemeCookie(), appearance));
  applyAppearanceSideEffects(appearance);
}

export function SiteAppearanceProvider({ children }: { children: ReactNode }) {
  // Same default on SSR and first client paint — reading sessionStorage in useState()
  // causes React #418 when cache differs from SITE_APPEARANCE_DEFAULT.
  const [appearance, setAppearance] = useState<SiteAppearancePublic>(SITE_APPEARANCE_DEFAULT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await fetchSiteAppearance();
    setAppearance(next);
    cacheAppearance(next);
    applyMergedTheme(next);
  }, []);

  useLayoutEffect(() => {
    const cached = readCachedAppearance();
    if (cached) {
      setAppearance(cached);
      applyMergedTheme(cached);
    } else {
      applyTheme(resolveEffectiveTheme(readThemeCookie(), SITE_APPEARANCE_DEFAULT));
    }

    refresh()
      .catch(() => {
        applyMergedTheme(SITE_APPEARANCE_DEFAULT);
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  const value = useMemo(
    () => ({ appearance, loading, refresh }),
    [appearance, loading, refresh],
  );

  return <SiteAppearanceContext.Provider value={value}>{children}</SiteAppearanceContext.Provider>;
}

export function useEffectiveThemeState() {
  const { appearance } = useSiteAppearance();
  return resolveEffectiveTheme(readThemeCookie(), appearance);
}

export { THEME_DEFAULT };
